import { TypeAttribute } from '@trace/types';
import groupBy from 'lodash.groupby';
import { useContext, useState, useEffect } from 'react';
import { URLStateContext } from '../URLState/URLState';
import { ATTRIBUTE_FILTER_ORDER } from '../../constants/Attributes';

export interface TypeFilterLabel {
  type: TypeAttribute;
  value: string;
}

interface HookResult {
  clearFilters: () => void;
  deselectFilter: (filter: TypeFilterLabel) => void;
  filters: TypeFilterLabel[];
  filtersGrouped: [TypeAttribute, TypeFilterLabel[]][];
  isFilterSelected: (filter: TypeFilterLabel) => boolean;
  selectFilter: (filter: TypeFilterLabel, deselect?: boolean | undefined) => void;
  toggleFilter: (filter: TypeFilterLabel) => void;
}

const getGroupedFilters = (filters: TypeFilterLabel[]) =>
  Object.entries(groupBy(filters, 'type')).sort(
    ([a], [b]) =>
      ATTRIBUTE_FILTER_ORDER.indexOf(a as TypeAttribute) -
      ATTRIBUTE_FILTER_ORDER.indexOf(b as TypeAttribute),
  ) as [TypeAttribute, TypeFilterLabel[]][];

const useFilters = (): HookResult => {
  const { filters, updateUrlState } = useContext(URLStateContext);
  const [filtersGrouped, setFiltersGrouped] = useState(getGroupedFilters(filters));

  const isFilterSelected = (filter: TypeFilterLabel) =>
    !!filters.find(({ type, value }) => filter.type === type && filter.value === value);

  const deselectFilter = (filter: TypeFilterLabel) => {
    updateUrlState({
      filters: filters.filter(({ type, value }) => filter.type !== type || filter.value !== value),
    });
  };

  const selectFilter = (filter: TypeFilterLabel, deselect?: boolean) => {
    if (!isFilterSelected(filter)) {
      updateUrlState({ filters: filters.concat([filter]) });
    } else if (deselect) {
      deselectFilter(filter);
    }
  };

  const toggleFilter = (filter: TypeFilterLabel) => {
    selectFilter(filter, true);
  };

  const clearFilters = () => {
    updateUrlState({ filters: [] });
  };

  useEffect(() => {
    setFiltersGrouped(getGroupedFilters(filters));
  }, [filters]);

  return {
    clearFilters,
    deselectFilter,
    filters,
    filtersGrouped,
    isFilterSelected,
    selectFilter,
    toggleFilter,
  };
};

export default useFilters;
